import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import { useCart } from '../../context/CartContext';
import OfflineService from '../../services/OfflineService';
import connectionService from '../../services/ConnectionService';
import { getPaymentProvider } from '../../services/PaymentProviderService';
import { closeBatch as closeGetnetBatch } from '../../services/GetnetService';

const FloatingLogoutButton = ({ onLogout }) => {
    const { settings, sessionData, clearSessionData } = useAppContext();
    const { clearCart } = useCart();
    const navigate = useNavigate();
    const [showPinModal, setShowPinModal] = useState(false);
    const [showClosingDataModal, setShowClosingDataModal] = useState(false);
    const [pin, setPin] = useState('');
    const [error, setError] = useState('');
    const [closingData, setClosingData] = useState(null);
    const [isOnline, setIsOnline] = useState(connectionService.getConnectionStatus());
    // eslint-disable-next-line no-unused-vars
    const [isVisible, setIsVisible] = useState(true);
    const [provider, setProvider] = useState('transbank');

    const apiUrl = settings?.apiUrl || 'http://localhost:9000';
    const isGetnet = provider === 'getnet';

    useEffect(() => {
        getPaymentProvider(apiUrl).then(setProvider);
    }, [apiUrl]);

    useEffect(() => {
        const unsubscribe = connectionService.subscribe((status) => {
            setIsOnline(status);
        });
        return unsubscribe;
    }, []);

    useEffect(() => {
        const checkFullscreen = () => {
            const availHeight = window.screen.availHeight;
            const availWidth = window.screen.availWidth;

            const heightRatio = window.innerHeight / availHeight;
            const widthRatio = window.innerWidth / availWidth;

            const isFullscreen = heightRatio > 0.90 && widthRatio > 0.90;
            setIsVisible(!isFullscreen);
        };

        checkFullscreen();

        const interval = setInterval(checkFullscreen, 1000);
        window.addEventListener('resize', checkFullscreen);

        return () => {
            clearInterval(interval);
            window.removeEventListener('resize', checkFullscreen);
        };
    }, []);

    const handleCloseTbk = async () => {
        if (!isOnline) {
            alert('No puedes cerrar la sesión en TBK en modo offline. Conéctate a internet para continuar.');
            return;
        }

        if (isGetnet) {
            try {
                const result = await closeGetnetBatch(apiUrl);
                if (!result?.approved) {
                    throw new Error(result?.message || 'El terminal no aprobó el cierre de lote');
                }
                alert('Cierre de lote Getnet realizado correctamente.');
            } catch (error) {
                console.error('Error en el cierre de lote Getnet:', error);
                alert('Error en el cierre de lote Getnet: '
                    + (error.response?.data?.error || error.message));
            }
            return;
        }

        try {
            const requestBody = {
                Port: "COM9",
                Printer: false,
            };

            const response = await fetch('http://localhost:8001/close_day', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
            });

            const result = await response.json();
            if (!response.ok) {
                throw new Error(result.message || 'Error al cerrar la sesión en TBK');
            }

            if (result.success !== undefined && !result.success) {
                throw new Error(result.message || 'Error desconocido al cerrar la sesión en TBK');
            }

            alert('Sesión cerrada exitosamente en TBK.');
        } catch (error) {
            console.error('Error al cerrar la sesión en TBK:', error);
            if (error.message === 'Failed to fetch') {
                alert('No se pudo conectar con el servidor TBK.');
            } else {
                alert('Error al cerrar la sesión en TBK: ' + error.message);
            }
        }
    };

    const handleLogout = () => {
        setShowPinModal(true);
    };

    const confirmPin = async () => {
        if (!isOnline) {
            setError('No puedes cerrar la sesión en modo offline. Conéctate a internet para continuar.');
            return;
        }

        try {
            const result = await OfflineService.closeSession(sessionData.session_id, pin, false);

            if (result.success) {
                setClosingData(result.closing_data);
                setShowPinModal(false);
                setShowClosingDataModal(true);
            } else {
                throw new Error(result.message || 'Error desconocido al cerrar la sesión');
            }
        } catch (error) {
            setError(error.message);
        }
    };

    const confirmClosing = async () => {
        if (!isOnline) {
            setError('No puedes cerrar la sesión en modo offline. Conéctate a internet para continuar.');
            setShowClosingDataModal(false);
            return;
        }

        try {
            const result = await OfflineService.closeSession(sessionData.session_id, pin, true);

            if (result.success) {
                setShowClosingDataModal(false);
                setPin('');
                setError('');

                clearCart();
                clearSessionData();
                OfflineService.clearSessionInfo();
                OfflineService.clearProducts();

                if (onLogout) {
                    onLogout();
                }

                navigate('/login', { replace: true });
            } else {
                throw new Error(result.message || 'Error desconocido al finalizar el cierre de la sesión');
            }
        } catch (error) {
            setError(error.message);
        }
    };

    return null;

    // eslint-disable-next-line no-unreachable
    return (
        <>
            <button
                onClick={handleLogout}
                className="fixed top-4 right-4 px-4 py-2 rounded-md text-sm font-bold transition-all duration-200 shadow-lg text-white hover:scale-105 hover:shadow-xl z-50"
                style={{ backgroundColor: '#6b7280', border: 'none' }}
            >
                Cerrar sesión
            </button>

            {showPinModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm w-full animate-fade-in">
                        <h2 className="text-lg font-semibold mb-4">Confirmar Cierre de Sesión</h2>
                        <p className="text-sm text-gray-600 mb-4">Por favor, ingresa el PIN para cerrar la sesión.</p>
                        <input
                            type="text"
                            value={pin}
                            onChange={(e) => setPin(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md mb-4 focus:outline-none focus:ring-2"
                            placeholder="Ingresa el PIN"
                        />
                        {error && <p className="text-sm text-red-600 mb-4">{error}</p>}
                        <div className="flex justify-end space-x-2">
                            <button
                                onClick={() => {
                                    setShowPinModal(false);
                                    setPin('');
                                    setError('');
                                }}
                                className="px-4 py-2 rounded-md text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 transition-colors duration-200"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={confirmPin}
                                className="px-4 py-2 rounded-md text-sm font-medium text-white transition-all duration-200 hover:scale-105"
                                style={{ backgroundColor: '#6b7280' }}
                            >
                                Confirmar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showClosingDataModal && closingData && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg shadow-lg max-w-lg w-full animate-fade-in">
                        <h2 className="text-lg font-semibold mb-4">Resumen de Cierre</h2>
                        <div className="space-y-2 mb-4 p-4 bg-gray-50 rounded-lg">
                            <p className="flex justify-between">
                                <strong>Total Pagos:</strong>
                                <span>${closingData.total_payments || 0}</span>
                            </p>
                            <p className="flex justify-between">
                                <strong>Número de Órdenes:</strong>
                                <span>{closingData.order_count || 0}</span>
                            </p>
                        </div>
                        {error && <p className="text-sm text-red-600 mb-4">{error}</p>}
                        <div className="flex flex-col space-y-2">
                            <div className="flex space-x-2">
                                <button
                                    onClick={() => {
                                        setShowClosingDataModal(false);
                                        setPin('');
                                        setError('');
                                    }}
                                    className="flex-1 py-2 px-4 rounded-md text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 transition-colors duration-200"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleCloseTbk}
                                    className="flex-1 py-2 px-4 rounded-md text-sm font-medium text-white transition-all duration-200 hover:scale-105"
                                    style={{ backgroundColor: '#28a745' }}
                                >
                                    {isGetnet ? 'Cerrar Lote Getnet' : 'Cerrar Sesión TBK'}
                                </button>
                            </div>
                            <button
                                onClick={confirmClosing}
                                className="w-full py-2 px-4 rounded-md text-sm font-medium text-white transition-all duration-200 hover:scale-105"
                                style={{ backgroundColor: '#6b7280' }}
                            >
                                Cerrar Sesión Total
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style jsx>{`
                @keyframes fade-in {
                    0% {
                        opacity: 0;
                        transform: scale(0.9);
                    }
                    100% {
                        opacity: 1;
                        transform: scale(1);
                    }
                }

                .animate-fade-in {
                    animation: fade-in 0.3s ease-out;
                }
            `}</style>
        </>
    );
};

export default FloatingLogoutButton;